import { Quaternion } from './quaternion.js';
import { Joint } from './joint';
import { Vector3 } from '../util/vector-utils.js';
import { log, format, FACILITY as LOG_FACILITY } from '../util/log';
import { roundFloat } from '../util/math-util.js';
var LOG = LOG_FACILITY.BODY;
export function toPersistanceFormat(el) {
    if (el instanceof Vector3) {
        return el.toArray().map(function (e) { return roundFloat(e, 3); });
    }
    var ret = Object.keys(el).reduce(function (acc, partName) {
        acc[partName] = toPersistanceFormat(el[partName]);
        return acc;
    }, {});
    return ret;
}
var BodyPart = (function () {
    function BodyPart(params) {
        var _this = this;
        this.getInitialRotation = function () {
            return _this.qInitial.toVector();
        };
        this.setInitialRotation = function (q) {
            log.info(LOG, _this.name + " setInitialRotation " + format(q));
            _this.qInitial = new Quaternion(q);
            _this.qInitialConjugate = _this.qInitial.conjugate();
            _this.updateAccelTransform();
        };
        this.setAlignment = function (qInitial, qAxisTransform) {
            log.info(LOG, _this.name + " setAlignment " + format(qInitial) + " " + format(qAxisTransform));
            if (qInitial) {
                _this.setInitialRotation(qInitial);
            }
            if (qAxisTransform) {
                _this.setAxisTransform(qAxisTransform);
            }
        };
        this.updateModel = function () {
            if (_this.qSensor !== Quaternion.ONE && _this.qInitialConjugate === Quaternion.ONE) {
                log.warn(LOG, "Alignment not performed on " + _this.name + " using current qSensor as initial rotation");
                _this.setInitialRotation(_this.qSensor.toVector());
            }
            var delta = _this.qSensor.mul(_this.qInitialConjugate);
            _this.qModel = delta.transform(_this.qModelTransform).transform(_this.qAxisTransform);
            _this.qModel = _this.qModel.round(8);
            if (_this.accelSensor && _this.angVelSensor) {
                _this.accelAnatomical = new Vector3(_this.qAnatomicalTransform.rotateVector(_this.accelSensor.toArray()));
                _this.angVelAnatomical = new Vector3(_this.qAnatomicalTransform.rotateVector(_this.angVelSensor.toArray()));
                _this.accelModel = new Vector3(_this.qModel.rotateVector(_this.accelAnatomical.toArray()));
                _this.angVelModel = new Vector3(_this.qModel.rotateVector(_this.angVelAnatomical.toArray()));
                _this.accelAnatomical = _this.accelAnatomical.round(8);
                _this.accelModel = _this.accelModel.round(8);
                _this.angVelAnatomical = _this.angVelAnatomical.round(8);
                _this.angVelModel = _this.angVelModel.round(8);
            }
        };
        this.reading = function (timestamp, reading, acceleration, angularVelocity) {
            _this.timestamp = timestamp;
            _this.qSensor = new Quaternion(reading);
            if (acceleration && angularVelocity) {
                _this.accelSensor = new Vector3(acceleration);
                _this.angVelSensor = new Vector3(angularVelocity);
            }
        };
        this.params = params;
        this.name = params.name;
        this.width = params.dims[0];
        this.height = params.dims[1];
        this.mass = params.mass;
        this.pCenter = new Vector3();
        this.pCenterMass = new Vector3();
        this.proximal = new Vector3();
        this.distal = {};
        this.joints = {};
        this.jointVector = {};
        this.axialVector = new Vector3(0, 0, 1);
        this.axial = new Vector3();
        this.cmVector = new Vector3(0, 0, this.params.cmZ);
        this.aligned = false;
        this.qInitial = Quaternion.ONE;
        this.qInitialConjugate = Quaternion.ONE;
        this.qSensor = Quaternion.ONE;
        this.qModelTransform = Quaternion.ONE;
        this.qAxisTransform = Quaternion.ONE;
        this.qModel = Quaternion.ONE;
        this.qAnatomicalTransform = Quaternion.ONE;
        this.accelSensor = new Vector3();
        this.accelAnatomical = new Vector3();
        this.accelModel = new Vector3();
        this.angVelSensor = new Vector3();
        this.angVelAnatomical = new Vector3();
        this.angVelModel = new Vector3();
        this.timestamp = 0;
        if (this.params.side === 'middle') {
            this.qModelTransform = new Quaternion(Math.SQRT1_2, 0, 0, -Math.SQRT1_2);
        }
        else if (this.params.side === 'left') {
            this.qModelTransform = new Quaternion(Math.SQRT1_2, 0, 0, -Math.SQRT1_2);
        }
        else {
            this.qModelTransform = new Quaternion(Math.SQRT1_2, 0, 0, Math.SQRT1_2);
        }
    }
    BodyPart.prototype.getModelStateElements = function () {
        var result = [
            this.qModel.toVector(),
            this.qSensor.toVector(),
            toPersistanceFormat(this.proximal),
            toPersistanceFormat(this.distal),
            toPersistanceFormat(this.pCenter),
            toPersistanceFormat(this.accelSensor),
            toPersistanceFormat(this.angVelSensor),
        ];
        return result;
    };
    BodyPart.prototype.setAxisTransform = function (qAxisTransform) {
        this.qAxisTransform = new Quaternion(qAxisTransform);
        this.aligned = true;
        this.updateAccelTransform();
    };
    BodyPart.prototype.clearAxisTransform = function () {
        this.qAxisTransform = Quaternion.ONE;
        this.aligned = false;
        this.updateAccelTransform();
    };
    BodyPart.prototype.updateAccelTransform = function () {
        this.qAnatomicalTransform = this.qAxisTransform.mul(this.qModelTransform.mul(this.qInitial));
    };
    BodyPart.prototype.addChild = function (part) {
        if (this.joints[part.name] !== undefined) {
            log.error(LOG, 'ERROR', part.name, 'added to model twice');
            return;
        }
        this.distal[part.name] = new Vector3();
        this.joints[part.name] = new Joint(part);
    };
    BodyPart.prototype.addChildren = function (children) {
        var _this = this;
        children.forEach(function (child) {
            _this.addChild(child);
        });
    };
    return BodyPart;
}());
export { BodyPart };
//# sourceMappingURL=body-part.js.map